import { useMutation } from '@apollo/client';
import { useMelwaysInfo } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import React, { useEffect } from 'react';

import { browserLogError } from 'src/modules/error-logger/browserLog';
import { resubscribeApacSavedSearchByCode } from 'src/modules/graphql/notifications/saved-search.graphql';
import { useTypedSelector } from 'src/reducers';
import type {
  ResubscribeApacSavedSearchByCodeMutation,
  ResubscribeApacSavedSearchByCodeMutationVariables,
} from 'src/types/graphql';

import Subscription from '../common/subscription';
import PageLoader from '../page-loader';
import { useRoutes } from '../routes';
import { trackSubscribeSuccessPageLoad } from '../tracking/tealium';

import translations from './.vocab';
import resubscribeIcon from './img/resubscribe-icon.png';
import SubscribeError from './subscribe-error';

const SubscribeJobAlert = (props: {
  token: string;
  authenticationStatus: string;
}) => {
  const { t } = useTranslations(translations);
  const { homePath, savedSearchesPath } = useRoutes();
  const { language } = useMelwaysInfo();

  const { token, authenticationStatus } = props;

  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);
  const [resubscribeJobAlert, { data, error }] = useMutation<
    ResubscribeApacSavedSearchByCodeMutation,
    ResubscribeApacSavedSearchByCodeMutationVariables
  >(resubscribeApacSavedSearchByCode);

  const authenticated = authenticationStatus === 'authenticated';
  const savedSearchPayload = data?.resubscribeApacSavedSearchByCode;
  const isFailurePayload = savedSearchPayload && 'error' in savedSearchPayload;

  useEffect(() => {
    if (token) {
      resubscribeJobAlert({ variables: { input: { code: token } } }).catch(
        (e) => {
          browserLogError(e);
        },
      );
    }
  }, [resubscribeJobAlert, token]);

  if (error || isFailurePayload) {
    return <SubscribeError language={language} zone={zone} linkMessage=" " />;
  }

  if (savedSearchPayload && !isFailurePayload) {
    trackSubscribeSuccessPageLoad('jobAlert', 'email');

    return (
      <Subscription
        title={t('ja-subscription-title')}
        cardTitle={t('ja-subscription-card-title')}
        body={
          authenticated
            ? t('ja-subscription-body-authenticated')
            : t('ja-subscription-body-unauthenticated')
        }
        icon={resubscribeIcon}
        link={authenticated ? savedSearchesPath : homePath}
        linkMessage={
          authenticated
            ? t('ja-subscription-link-message-authenticated')
            : t('ja-subscription-link-message-unauthenticated')
        }
      />
    );
  }

  return <PageLoader />;
};

export default SubscribeJobAlert;
